import type { PublicKey } from '@shared/types';
import {
  clearBridgeMasterKey,
  isWebViewMode,
  patchBridgeIdentityMeta,
  removeBridgeDevice,
  removeBridgeIdentity,
  updateBridgeDevice,
  updateBridgeIdentity,
  updateBridgeMasterKey
} from '../utils/webviewBridge';
import type { IdentityAccessKind } from '../utils/identityAccess';

type BridgeIdentityMeta = {
  publicKey?: string;
  serverUrl?: string;
  serverName?: string;
  identityName?: string;
  publishIdentity?: boolean;
  presenceVisible?: boolean;
  role?: string;
  accessKind?: IdentityAccessKind;
  accessContextLabel?: string;
  vpsId?: string;
  isServerAdmin?: boolean;
  isPrimaryVpsTarget?: boolean;
  fingerprint?: string;
  serverId?: string;
  noNamesOnServer?: boolean;
  useForBackupStorage?: boolean;
  pendingRegistration?: boolean;
};

export function persistBridgeMasterKey(rawBytes: Uint8Array): void {
  if (!isWebViewMode) return;
  updateBridgeMasterKey(rawBytes);
}

export function clearBridgeMasterKeyState(): void {
  if (!isWebViewMode) return;
  clearBridgeMasterKey();
}

export function upsertBridgeIdentity(params: {
  identityId: string;
  seed: Uint8Array;
  publicKey: PublicKey;
  serverUrl?: string;
  serverName?: string;
  identityName?: string;
  publishIdentity?: boolean;
  presenceVisible?: boolean;
  role?: string;
  accessKind?: IdentityAccessKind;
  accessContextLabel?: string;
  vpsId?: string;
  isServerAdmin?: boolean;
  isPrimaryVpsTarget?: boolean;
  fingerprint?: string;
  serverId?: string;
  noNamesOnServer?: boolean;
  useForBackupStorage?: boolean;
  pendingRegistration?: boolean;
}): void {
  if (!isWebViewMode) return;
  if (!params.serverUrl) return;

  updateBridgeIdentity(params.identityId, params.seed, {
    publicKey: params.publicKey.value,
    serverUrl: params.serverUrl,
    serverName: params.serverName,
    identityName: params.identityName,
    publishIdentity: params.publishIdentity,
    presenceVisible: params.presenceVisible,
    role: params.role,
    accessKind: params.accessKind,
    accessContextLabel: params.accessContextLabel,
    vpsId: params.vpsId,
    isServerAdmin: params.isServerAdmin,
    isPrimaryVpsTarget: params.isPrimaryVpsTarget,
    fingerprint: params.fingerprint,
    serverId: params.serverId,
    noNamesOnServer: params.noNamesOnServer,
    useForBackupStorage: params.useForBackupStorage,
    pendingRegistration: params.pendingRegistration
  });
}

export function patchBridgeIdentityState(
  identityId: string,
  patch: BridgeIdentityMeta
): void {
  if (!isWebViewMode) return;
  if (Object.keys(patch).length === 0) return;
  patchBridgeIdentityMeta(identityId, patch);
}

export function removeBridgeIdentityState(identityId: string): void {
  if (!isWebViewMode) return;
  removeBridgeIdentity(identityId);
}

export function upsertBridgeDevice(params: {
  deviceId: string;
  identityId: string;
  publicKey: PublicKey;
  seed: Uint8Array;
  label?: string;
  encryptionPublicKey?: PublicKey | null;
  encryptionSeed?: Uint8Array;
  pendingRegistration?: boolean;
}): void {
  if (!isWebViewMode) return;
  updateBridgeDevice(
    params.deviceId,
    params.identityId,
    params.publicKey.value,
    params.seed,
    params.label,
    params.encryptionPublicKey?.value,
    params.encryptionSeed,
    params.pendingRegistration
  );
}

export function removeBridgeDeviceState(deviceId: string): void {
  if (!isWebViewMode) return;
  removeBridgeDevice(deviceId);
}
